import { Commodities, ContractType, OtcContractLayerDto } from "@/lib/api/otc";
import {
  ContractFuturesStep,
  getStepsForContractType,
  isCanolaContractWithFuturesRows,
} from "@/section/otc/OtcBearState";
import { OtcLayerFormValues } from "./snapshotToForm";

/**
 * Layer-native field RELEVANCE rules for the v3 current-layer editor.
 *
 * This module decides whether a field is relevant to the current contract
 * state (contract_type / commodity). It is the v3 counterpart to the v2 form's
 * per-field `visible` predicates, but it is intentionally independent of v2's
 * `buildFieldMeta` / schema machinery.
 *
 * The contract between the three layers of truth:
 * - `owned_fields` (backend): decides whether the layer MAY edit a field.
 * - `shouldShowOtcLayerField` (this module): decides whether the field is
 *   RELEVANT to the current contract state. Driven by live form values so
 *   toggling contract_type / commodity updates visibility immediately.
 * - backend validation: the final source of truth on save.
 *
 * `OtcLayerForm` renders a field only when it is in the registry AND owned AND
 * relevant.
 */

/**
 * Backend-derived fields. The v3 registry already excludes these, but they are
 * listed here defensively so that if one ever leaks into the registry or
 * `owned_fields` it still never renders as an editable input. The backend
 * recomputes all of them.
 */
const DERIVED_FIELDS = new Set<string>([
  "margin",
  "margin_amount",
  "adj_margin",
  "adj_margin_amount",
  "adj_margin_two",
  "adj_margin_amount_two",
  "adj_margin_three",
  "adj_margin_amount_three",
  "expected_contract_futures",
  "expected_contract_futures_step_one",
  "expected_contract_futures_step_two",
  "expected_contract_futures_double_up",
  "futures_margin",
  "futures_margin_step_one",
  "futures_margin_step_two",
  "futures_margin_double_up",
  "final_futures_price_margin",
]);

/**
 * Normal base fields: relevant whenever the layer owns them, regardless of
 * contract type / commodity.
 */
const BASE_FIELDS = new Set<string>([
  "date",
  "end_date",
  "contract_type",
  "customer_id",
  "commodity",
  "option_month",
  "quantity",
  "trade_reference",
  "account_no_a",
  "option_premium",
  "contract_fee",
  "contract_no",
  "contract_reference",
  "shipment_start_date",
  "shipment_end_date",
  "trader_id",
  "amendment_trader_id",
  "crop_year",
  "notes",
  "has_invoice_fees",
  "has_contract_adjustment",
]);

/**
 * Layer/event metadata fields. Not `trading_contract` columns and not in v2
 * schemas; relevant whenever owned (e.g. `exit_date` on an exit layer).
 */
const LAYER_METADATA_FIELDS = new Set<string>(["exit_date"]);

/**
 * Roll / basis fields. Owned by roll amendment layers; relevance is decided by
 * ownership, not by base contract type / commodity.
 */
const ROLL_FIELDS = new Set<string>([
  "rolled_option_month",
  "carry",
  "rolled_basis_month",
  "basis_carry",
]);

/**
 * Adjustment slot fields (slots 1–3). The backend assigns which slot belongs to
 * the layer via `owned_fields`, so relevance is decided by ownership.
 */
const ADJUSTMENT_FIELDS = new Set<string>([
  "adj_option_premium",
  "adj_contract_fee",
  "last_adjusted_date",
  "adj_option_premium_two",
  "adj_contract_fee_two",
  "adjusted_date_two",
  "adj_option_premium_three",
  "adj_contract_fee_three",
  "adjusted_date_three",
]);

/** Accumulator-only fields. */
const ACCUMULATOR_FIELDS = new Set<string>([
  "start_date",
  "weeks",
  "period_qty",
  "knockout",
]);

/** Premium Offer W/DU-only fields. */
const PREMIUM_OFFER_FIELDS = new Set<string>([
  "account_no_b",
  "futures_price",
  "otc_futures",
]);

/**
 * Contract types that use the second / HTA option month (`option_month_etc_one`).
 */
export const CONTRACT_TYPES_WITH_OPTION_MONTH_ETC_ONE = new Set<string>([
  ContractType.RangeWDU,
  ContractType.ReOwnWDU,
  ContractType.PremiumOfferWDU,
  ContractType.DoubleUp,
  ContractType.AccWeeklyDU,
  ContractType.MinMaxWDU,
]);

/** Step field name → its futures step key. */
const STEP_FIELD_BY_NAME: Record<string, ContractFuturesStep> = {
  step_one: "step_one",
  step_two: "step_two",
  step_three: "step_three",
  double_up: "double_up",
};

/** Canola "quoted/actual futures" field name → its futures step key. */
const CANOLA_ACTUAL_FUTURES_FIELD_BY_NAME: Record<string, ContractFuturesStep> = {
  actual_contract_futures_step_one: "step_one",
  actual_contract_futures_step_two: "step_two",
  actual_contract_futures: "step_three",
  actual_contract_futures_double_up: "double_up",
};

/** Canola "final futures" row fields (shown together for Canola w/ steps). */
const CANOLA_FINAL_FUTURES_FIELDS = new Set<string>([
  "final_futures_price",
  "final_futures_expected_price",
  "final_futures_price_date",
]);

/**
 * Decides whether a field is relevant to the current contract state.
 *
 * Uses live `values` (not the layer snapshot) so changing `contract_type` or
 * `commodity` updates visibility immediately, matching v2. Does NOT consult
 * `owned_fields`; the caller (`OtcLayerForm`) gates editability on ownership
 * separately.
 *
 * The `layer` param is currently unused but kept in the signature for future
 * rules that may depend on `source_type` / `amendment_type` / `adjustment_slot`.
 */
export function shouldShowOtcLayerField(params: {
  field_name: string;
  values: OtcLayerFormValues;
  layer: OtcContractLayerDto;
}): boolean {
  const { field_name, values } = params;

  const contractType =
    typeof values.contract_type === "string" ? values.contract_type : undefined;
  const commodity =
    typeof values.commodity === "string" ? values.commodity : undefined;

  if (DERIVED_FIELDS.has(field_name)) {
    return false;
  }

  if (BASE_FIELDS.has(field_name)) {
    return true;
  }

  if (LAYER_METADATA_FIELDS.has(field_name)) {
    return true;
  }

  if (ROLL_FIELDS.has(field_name)) {
    return true;
  }

  if (ADJUSTMENT_FIELDS.has(field_name)) {
    return true;
  }

  const step = STEP_FIELD_BY_NAME[field_name];
  if (step) {
    return getStepsForContractType(contractType).includes(step);
  }

  if (field_name === "implied_fx_amount") {
    return commodity === Commodities.Canola;
  }

  const futuresStep = CANOLA_ACTUAL_FUTURES_FIELD_BY_NAME[field_name];
  if (futuresStep) {
    return (
      commodity === Commodities.Canola &&
      getStepsForContractType(contractType).includes(futuresStep)
    );
  }

  if (CANOLA_FINAL_FUTURES_FIELDS.has(field_name)) {
    return isCanolaContractWithFuturesRows({
      commodity,
      contract_type: contractType,
    });
  }

  if (field_name === "option_month_etc_one") {
    return (
      !!contractType &&
      CONTRACT_TYPES_WITH_OPTION_MONTH_ETC_ONE.has(contractType)
    );
  }

  if (PREMIUM_OFFER_FIELDS.has(field_name)) {
    return contractType === ContractType.PremiumOfferWDU;
  }

  if (ACCUMULATOR_FIELDS.has(field_name)) {
    return contractType === ContractType.AccWeeklyDU;
  }

  return false;
}

/**
 * Fields cleared when commodity changes away from Canola. Mirrors the v2
 * commodity `effects` in field-meta.tsx (minus derived fields, which v3 never
 * holds in form state).
 */
const CANOLA_FIELDS_TO_CLEAR_OFF_CANOLA: ReadonlyArray<string> = [
  "implied_fx_amount",
  "actual_contract_futures",
  "actual_contract_futures_step_one",
  "actual_contract_futures_step_two",
  "actual_contract_futures_double_up",
  "final_futures_price",
  "final_futures_expected_price",
  "final_futures_price_date",
];

/** All step fields, used when narrowing to a new contract type's active steps. */
const ALL_STEP_FIELDS: ReadonlyArray<string> = [
  "step_one",
  "step_two",
  "step_three",
  "double_up",
];

/**
 * Computes field updates to apply after a discriminator field
 * (`contract_type` / `commodity`) changes, mirroring v2's clearing effects.
 *
 * Only fields the layer OWNS are cleared, so an edit cannot mutate a field the
 * backend would reject. Cleared fields are set to `undefined`; the patch
 * builder then emits an explicit `null` for any that previously had a value.
 *
 * Returns a partial of changed fields (never includes the changed field
 * itself). Returns an empty object when nothing needs clearing.
 */
export function computeOtcLayerFieldEffects(params: {
  field_name: string;
  next_value: unknown;
  prev_values: OtcLayerFormValues;
  owned_fields: ReadonlyArray<string>;
}): Partial<OtcLayerFormValues> {
  const { field_name, next_value, prev_values, owned_fields } = params;
  const owned = new Set(owned_fields);
  const patch: Partial<OtcLayerFormValues> = {};

  const clearIfOwned = (name: string) => {
    if (owned.has(name) && prev_values[name] !== undefined) {
      patch[name] = undefined;
    }
  };

  if (field_name === "commodity") {
    const nextCommodity = typeof next_value === "string" ? next_value : undefined;
    if (
      prev_values.commodity === Commodities.Canola &&
      nextCommodity !== Commodities.Canola
    ) {
      for (const name of CANOLA_FIELDS_TO_CLEAR_OFF_CANOLA) {
        clearIfOwned(name);
      }
    }
    return patch;
  }

  if (field_name === "contract_type") {
    const nextType = typeof next_value === "string" ? next_value : undefined;
    const activeSteps = getStepsForContractType(nextType);

    // Clear step fields (and their Canola quoted-futures counterparts) that the
    // new contract type does not use.
    for (const stepField of ALL_STEP_FIELDS) {
      const stepKey = STEP_FIELD_BY_NAME[stepField];
      if (!activeSteps.includes(stepKey)) {
        clearIfOwned(stepField);
        for (const [futuresField, futuresStep] of Object.entries(
          CANOLA_ACTUAL_FUTURES_FIELD_BY_NAME,
        )) {
          if (futuresStep === stepKey) {
            clearIfOwned(futuresField);
          }
        }
      }
    }

    // Clear the final futures row when the new type has no futures steps at all.
    if (activeSteps.length === 0) {
      for (const name of CANOLA_FINAL_FUTURES_FIELDS) {
        clearIfOwned(name);
      }
    }

    // Clear Premium Offer-only fields when leaving Premium Offer W/DU.
    if (nextType !== ContractType.PremiumOfferWDU) {
      for (const name of PREMIUM_OFFER_FIELDS) {
        clearIfOwned(name);
      }
    }

    // Clear accumulator-only fields when leaving Accumulator Weekly/DU.
    if (nextType !== ContractType.AccWeeklyDU) {
      for (const name of ACCUMULATOR_FIELDS) {
        clearIfOwned(name);
      }
    }

    // Clear the HTA/second option month when the new type does not use it.
    if (!nextType || !CONTRACT_TYPES_WITH_OPTION_MONTH_ETC_ONE.has(nextType)) {
      clearIfOwned("option_month_etc_one");
    }

    return patch;
  }

  return patch;
}
